import React, { useState } from 'react';
import { AlertCircle, Copy, X, Key, CheckCircle } from 'lucide-react';

const LicenseModal = ({ isOpen, onClose, licenseKey, licenseType = 'paid', expirationDate }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(licenseKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatExpirationDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isTrialLicense = licenseType === 'trial' || licenseKey.includes('TRIAL');

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full relative">

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
              <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {isTrialLicense ? 'Trial License Ready' : 'License Activated'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isTrialLicense ? 'Your 7-day trial has started' : 'Your paid license is now active'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">

          {/* License Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Your {isTrialLicense ? 'Trial' : 'Professional'} License Key
            </label>
            <div className="relative">
              <input
                type="text"
                value={licenseKey}
                readOnly
                className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              />
              <button
                onClick={handleCopyToClipboard}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                title="Copy to clipboard"
              >
                {copied
                  ? <CheckCircle className="w-5 h-5 text-green-500" />
                  : <Copy className="w-5 h-5" />
                }
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              Click the copy icon to copy your license key to clipboard
            </p>
          </div>

          {/* Expiration Info */}
          {expirationDate && (
            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-center mb-1.5">
                <AlertCircle className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2 flex-shrink-0" />
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isTrialLicense ? 'Trial Period' : 'License Period'}
                </h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Expires on <span className="font-medium text-gray-900 dark:text-white">{formatExpirationDate(expirationDate)}</span>
              </p>
            </div>
          )}

          {/* Important note */}
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-300">Important:</span> Save this license key securely. You'll need it to activate the Stonewall Invoicing desktop application.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              onClick={() => { window.location.href = '/downloads'; }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              Download Stonewall Invoicing
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              Close
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LicenseModal;
